import { useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { registerPendingTextEdit } from "../utils/pendingTextEdits";

interface UseBufferedTextCommitOptions {
  value: string;
  onCommit: (nextValue: string) => void;
  debounceMs?: number;
  flushOnCleanupKeys?: readonly unknown[];
}

export default function useBufferedTextCommit({
  value,
  onCommit,
  debounceMs = 500,
  flushOnCleanupKeys = [],
}: UseBufferedTextCommitOptions) {
  const [currentValue, setCurrentValue] = useState(value);
  const latestValueRef = useRef(value);
  const latestCommitRef = useRef(onCommit);
  const pendingCleanupRef = useRef<(() => void) | null>(null);
  const hasPendingRef = useRef(false);

  useEffect(() => {
    latestCommitRef.current = onCommit;
  }, [onCommit]);

  useEffect(() => {
    latestValueRef.current = currentValue;
  }, [currentValue]);

  useEffect(() => {
    if (!hasPendingRef.current) {
      setCurrentValue(value);
      latestValueRef.current = value;
    }
  }, [value]);

  const clearPendingRegistration = () => {
    pendingCleanupRef.current?.();
    pendingCleanupRef.current = null;
    hasPendingRef.current = false;
  };

  const commitValue = (nextValue: string) => {
    latestCommitRef.current(nextValue);
    clearPendingRegistration();
  };

  const debouncedCommit = useDebouncedCallback((nextValue: string) => {
    commitValue(nextValue);
  }, debounceMs);

  const flushPendingValue = () => {
    if (!hasPendingRef.current) {
      return;
    }

    debouncedCommit.flush?.();

    if (hasPendingRef.current) {
      commitValue(latestValueRef.current);
    }
  };

  const registerPendingValue = () => {
    pendingCleanupRef.current?.();
    pendingCleanupRef.current = registerPendingTextEdit(flushPendingValue);
    hasPendingRef.current = true;
  };

  const handleValueChange = (nextValue: string) => {
    latestValueRef.current = nextValue;
    setCurrentValue(nextValue);
    registerPendingValue();
    debouncedCommit(nextValue);
  };

  useEffect(() => {
    return () => {
      flushPendingValue();
      clearPendingRegistration();
    };
  }, [debouncedCommit, ...flushOnCleanupKeys]);

  return {
    currentValue,
    setCurrentValue,
    handleValueChange,
    flushPendingValue,
  };
}
